// services/frontend/src/hooks/usePhotoUpload.ts

import { useState } from 'react';
import { gql } from '@apollo/client';
import { PhotoUploadProgress } from '../types/photo';
import { apolloClient } from '../graphql/client';
import logger from '../utils/logger';

const MAX_FILE_SIZE = 524288000; // 500MB in bytes
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/tiff'];

// GraphQL mutations
const GET_UPLOAD_URL_MUTATION = gql`
  mutation GetUploadUrl($input: GetUploadUrlInput!) {
    getUploadUrl(input: $input) {
      uploadUrl
      key
    }
  }
`;

const CREATE_PHOTO_MUTATION = gql`
  mutation CreatePhoto($input: CreatePhotoInput!) {
    createPhoto(input: $input) {
      id
      globalId
      title
      userId
    }
  }
`;

interface UploadPhotoOptions {
  title: string;
  description?: string;
  file: File;
}

interface UploadResult {
  success: boolean;
  photoId?: string;
  error?: string;
  isDuplicate?: boolean; // Feature 047: Duplicate detection flag
}

export function usePhotoUpload() {
  const [uploadProgress, setUploadProgress] = useState<PhotoUploadProgress | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds 500MB limit. Selected file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`
      };
    }

    // Check MIME type
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type: ${file.type}. Only JPEG, PNG, and TIFF files are supported.`
      };
    }

    return { valid: true };
  };

  const uploadPhoto = async (options: UploadPhotoOptions): Promise<UploadResult> => {
    const { title, file } = options;

    logger.log('PhotoUpload', 'info', `Starting upload for file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      logger.log('PhotoUpload', 'error', `File validation failed: ${validation.error}`, { filename: file.name, size: file.size, type: file.type });
      return { success: false, error: validation.error };
    }

    setIsUploading(true);
    setUploadProgress({
      file,
      progress: 0,
      status: 'pending'
    });

    try {
      // Step 1: Request upload URL from API via GraphQL
      logger.log('PhotoUpload', 'debug', `Requesting upload URL for: ${file.name}`);
      setUploadProgress(prev => prev ? { ...prev, status: 'uploading', progress: 10 } : null);

      const uploadUrlResult = await apolloClient.mutate({
        mutation: GET_UPLOAD_URL_MUTATION,
        variables: {
          input: {
            filename: file.name,
            contentType: file.type
          }
        }
      });

      if (uploadUrlResult.errors) {
        throw new Error(uploadUrlResult.errors[0]?.message || 'Failed to get upload URL');
      }

      const uploadData = uploadUrlResult.data?.getUploadUrl;
      if (!uploadData) {
        throw new Error('No upload URL received');
      }

      logger.log('PhotoUpload', 'debug', `Upload URL received for: ${file.name}`, { storageKey: uploadData.key });

      const { uploadUrl, key: storageKey } = uploadData;

      // Step 2: Upload file to storage using pre-signed URL
      logger.log('PhotoUpload', 'debug', `Starting binary upload to storage for: ${file.name}`);
      setUploadProgress(prev => prev ? { ...prev, progress: 20 } : null);

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const uploadStartTime = Date.now();

        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            // Map 20% -> 70% of overall progress to upload phase
            const uploadPercent = (event.loaded / event.total) * 50 + 20;
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            const elapsedMs = Date.now() - uploadStartTime;
            const bytesPerMs = event.loaded / elapsedMs;
            const remainingBytes = event.total - event.loaded;
            const estimatedRemainingMs = remainingBytes / bytesPerMs;

            logger.log('PhotoUpload', 'debug', `Upload progress: ${percentComplete}% (${(event.loaded / 1024 / 1024).toFixed(2)}MB / ${(event.total / 1024 / 1024).toFixed(2)}MB), ETA: ${(estimatedRemainingMs / 1000).toFixed(1)}s`, { filename: file.name });
            setUploadProgress(prev => prev ? { ...prev, progress: Math.round(uploadPercent) } : null);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const uploadDurationMs = Date.now() - uploadStartTime;
            logger.log('PhotoUpload', 'info', `Binary upload completed successfully for: ${file.name} in ${(uploadDurationMs / 1000).toFixed(2)}s`, {
              status: xhr.status,
              duration: uploadDurationMs,
              size: file.size
            });
            resolve();
          } else {
            logger.log('PhotoUpload', 'error', `Binary upload failed with HTTP ${xhr.status} for: ${file.name}`, {
              status: xhr.status,
              statusText: xhr.statusText,
              responseText: xhr.responseText?.substring(0, 500) // Log first 500 chars of response
            });
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          logger.log('PhotoUpload', 'error', `Network error during binary upload for: ${file.name}`, {
            readyState: xhr.readyState,
            status: xhr.status
          });
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          logger.log('PhotoUpload', 'warn', `Upload aborted for: ${file.name}`);
          reject(new Error('Upload aborted'));
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        logger.log('PhotoUpload', 'debug', `Sending ${(file.size / 1024 / 1024).toFixed(2)}MB to storage for: ${file.name}`);
        xhr.send(file);
      });

      // Step 3: Create photo record in database via GraphQL
      logger.log('PhotoUpload', 'debug', `Creating photo record for: ${file.name}`, { storageKey });
      setUploadProgress(prev => prev ? { ...prev, status: 'processing', progress: 75 } : null);

      const createPhotoResult = await apolloClient.mutate({
        mutation: CREATE_PHOTO_MUTATION,
        variables: {
          input: {
            title,
            filename: file.name,
            storageKey,
            fileSize: file.size,
            mimeType: file.type
          }
        }
      });

      if (createPhotoResult.errors) {
        throw new Error(createPhotoResult.errors[0]?.message || 'Failed to create photo record');
      }

      const photo = createPhotoResult.data?.createPhoto;
      if (!photo) {
        throw new Error('No photo data received');
      }
      const isDuplicate = false; // GraphQL API doesn't return isDuplicate flag in this mutation

      if (isDuplicate) {
        logger.log('PhotoUpload', 'info', `Duplicate photo detected - reusing existing: ${photo.id}`, {
          photoId: photo.id,
          filename: file.name,
          title
        });
      } else {
        logger.log('PhotoUpload', 'info', `Photo record created successfully: ${photo.id}`, {
          photoId: photo.id,
          filename: file.name,
          title
        });
      }

      // Upload complete
      setUploadProgress(prev => prev ? { ...prev, status: 'complete', progress: 100, photoId: photo.id } : null);
      setIsUploading(false);

      logger.log('PhotoUpload', 'info', `Upload completed successfully for: ${file.name}`, { photoId: photo.id, isDuplicate });

      return {
        success: true,
        photoId: photo.id,
        isDuplicate
      };

    } catch (error) {
      // Enhanced error logging for debugging intermittent failures
      logger.error('PhotoUpload', 'Upload failed:', error);

      let errorMessage = 'Upload failed';

      if (error instanceof Error) {
        errorMessage = error.message;

        // Check for common error patterns
        if (errorMessage.includes('UNAUTHENTICATED') || errorMessage.includes('401')) {
          errorMessage = 'Authentication failed. Please log in again.';
          logger.log('PhotoUpload', 'error', 'Authentication error during upload');
        } else if (errorMessage.includes('FORBIDDEN') || errorMessage.includes('403')) {
          errorMessage = 'Access forbidden. You may not have permission for this action.';
          logger.log('PhotoUpload', 'error', 'Permission denied during upload');
        } else if (errorMessage.includes('Network error') || errorMessage.includes('fetch')) {
          errorMessage = 'Network error. Please check your internet connection.';
          logger.log('PhotoUpload', 'error', 'Network error during upload');
        }

        logger.log('PhotoUpload', 'error', 'Error during upload', {
          message: error.message,
          stack: error.stack,
          filename: file.name
        });
      } else {
        logger.log('PhotoUpload', 'error', 'Unknown error type during upload', {
          error,
          errorType: typeof error,
          filename: file.name
        });
      }

      setUploadProgress(prev => prev ? { ...prev, status: 'error', error: errorMessage } : null);
      setIsUploading(false);

      logger.log('PhotoUpload', 'error', `Upload failed for: ${file.name}`, { errorMessage });

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const resetUpload = () => {
    setUploadProgress(null);
    setIsUploading(false);
  };

  return {
    uploadPhoto,
    uploadProgress,
    isUploading,
    resetUpload
  };
}
